import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import GeoGate from './components/GeoGate';
import SkipToMain from './components/SkipToMain';
import ScrollProgress from './components/ScrollProgress';
import TopBar from './components/TopBar';
import Header from './components/Header';
import FlashSaleBanner from './components/FlashSaleBanner';
import FlashDealsSection from './components/FlashDealsSection';
import HeroCarousel from './components/HeroCarousel';
import TrustBadges from './components/TrustBadges';
import WhyChooseUs from './components/WhyChooseUs';
import BrowseCategories from './components/BrowseCategories';
import PromotionalBanner from './components/PromotionalBanner';
import ProductGrid from './components/ProductGrid';
import StatsCounter from './components/StatsCounter';
import TrustedBrands from './components/TrustedBrands';
import TestimonialsSection from './components/TestimonialsSection';
import NewsletterSection from './components/NewsletterSection';
import Footer from './components/Footer';
import HomePageSections from './components/HomePageSections';
import WelcomeModal from './components/WelcomeModal';
import CartDrawer from './components/CartDrawer';
import QuickViewModal from './components/QuickViewModal';
import FloatingActions from './components/FloatingActions';
import LivePurchaseNotification from './components/LivePurchaseNotification';
import ContactModal from './components/ContactModal';
import AuthModal from './components/AuthModal';
import ContentPageModal from './components/ContentPageModal';
import RecentlyViewed from './components/RecentlyViewed';
import TrendingProducts from './components/TrendingProducts';
import MobileBottomNav from './components/MobileBottomNav';
import AccessibilityPanel, { AccessibilityButton } from './components/AccessibilityPanel';
import { AccessibilityProvider } from './context/AccessibilityContext';
import { productsApi, cartApi, contentApi, categoriesApi, wishlistApi } from './services/api';
import { products as mockProducts, bestSellers as mockBestSellers } from './data/mock';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Lazy load heavy components
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const CustomerDashboard = lazy(() => import('./components/CustomerDashboard'));
const AffiliateDashboard = lazy(() => import('./components/AffiliateDashboard'));
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const AIChatbot = lazy(() => import('./components/AIChatbot'));
const PersonalizedSections = lazy(() => import('./components/PersonalizedSections'));
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt'));
const SearchResults = lazy(() => import('./components/SearchResults'));
const CategoryPage = lazy(() => import('./components/CategoryPage'));
const AllProductsPage = lazy(() => import('./components/AllProductsPage'));
const HealthTipsBlog = lazy(() => import('./components/HealthTipsBlog'));
const FAQSection = lazy(() => import('./components/FAQSection'));

// Loading fallback component
const DashboardLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// Generate or get session ID for cart
const getSessionId = () => {
  let sessionId = localStorage.getItem('cart_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('cart_session_id', sessionId);
  }
  return sessionId;
};

const HomePageContent = () => {
  const { user, isAdmin, isCustomer, isAffiliate, isAuthenticated } = useAuth();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [products, setProducts] = useState(mockProducts);
  const [bestSellers, setBestSellers] = useState(mockBestSellers);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState(mockProducts);
  const [sortBy, setSortBy] = useState('featured');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showContentPage, setShowContentPage] = useState(null);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showCustomerDashboard, setShowCustomerDashboard] = useState(false);
  const [showAffiliatePanel, setShowAffiliatePanel] = useState(false);
  const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);
  // Page navigation state
  const [activePage, setActivePage] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [activeCategorySlug, setActiveCategorySlug] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [productsVersion, setProductsVersion] = useState(0);
  const sessionId = getSessionId();

  // Product data transformer
  const transformProduct = (p) => ({
    id: p.id,
    name: p.name,
    generic_name: p.generic_name || '',
    price: p.sale_price || p.price,
    originalPrice: p.sale_price ? p.price : null,
    rating: p.rating || 4.5,
    reviewCount: p.review_count || 0,
    inStock: p.stock_quantity > 0,
    discount: p.sale_price ? Math.round((1 - p.sale_price / p.price) * 100) : 0,
    image: p.images && p.images[0] ? p.images[0] : 'https://via.placeholder.com/300',
    images: p.images || [],
    category: p.category_id,
    categorySlug: p.category_slug,
    description: p.description,
    brand: p.brand,
    is_featured: p.is_featured || false,
    is_best_seller: p.is_best_seller || false,
    is_monthly_offer: p.is_monthly_offer || false,
    created_at: p.created_at || null,
    badge: p.is_monthly_offer ? 'Offer' : (p.is_best_seller ? 'Best Seller' : (p.is_featured ? 'Featured' : null)),
  });

  // Reusable fetch function
  const refreshData = useCallback(async () => {
    try {
      const allProducts = await productsApi.getAll({ is_active: true, limit: 200 });
      if (allProducts && allProducts.length > 0) {
        const transformed = allProducts.map(transformProduct);
        setProducts(transformed);
        setFilteredProducts(transformed);
        const featured = transformed.filter(p => p.is_featured);
        setFeaturedProducts(featured.length > 0 ? featured : transformed);
        const bs = transformed.filter(p => p.is_best_seller);
        setBestSellers(bs.length > 0 ? bs : []);
      }
      const apiCategories = await categoriesApi.getAll();
      if (apiCategories && apiCategories.length > 0) {
        setCategories(apiCategories);
      }
      // Bump version so child pages re-fetch their own product/category data
      setProductsVersion((v) => v + 1);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  // Fetch products from API on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        await refreshData();

        // Fetch best sellers separately (API endpoint may return different ordering)
        const bestSellerProducts = await productsApi.getBestSellers();
        if (bestSellerProducts && bestSellerProducts.length > 0) {
          setBestSellers(bestSellerProducts.map(transformProduct));
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [refreshData]);

  // Fetch cart from API
  const fetchCart = useCallback(async () => {
    try {
      const cart = await cartApi.get(sessionId, user?.id);
      if (cart && cart.items) {
        setCartItems(cart.items.map(item => ({
          id: item.product_id,
          name: item.name,
          price: item.sale_price || item.price,
          image: item.image,
          quantity: item.quantity,
          stock: item.stock_quantity,
        })));
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [sessionId, user?.id]);

  // Fetch wishlist from API
  const fetchWishlist = useCallback(async () => {
    if (!user?.id) return;
    try {
      const wishlist = await wishlistApi.get(user.id);
      if (wishlist && wishlist.items) {
        setWishlistItems(wishlist.items.map(item => item.product_id));
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Show welcome modal after 30 seconds on first visit (only if not logged in)
  // Longer delay ensures users can explore the site before seeing the modal
  // Don't show welcome modal for authenticated users
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      const hasSeenWelcome = sessionStorage.getItem('hasSeenWelcome');
      if (!hasSeenWelcome) {
        const timer = setTimeout(() => {
          // Double check user is still not authenticated
          if (!localStorage.getItem('medicareToken')) {
            setShowWelcomeModal(true);
            sessionStorage.setItem('hasSeenWelcome', 'true');
          }
        }, 30000);
        return () => clearTimeout(timer);
      }
    } else if (isAuthenticated) {
      // Close welcome modal if user logs in
      setShowWelcomeModal(false);
    }
  }, [isAuthenticated, isLoading]);

  // Track recently viewed products
  const trackRecentlyViewed = (productId) => {
    const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    const updated = [productId, ...viewed.filter((id) => id !== productId)].slice(0, 10);
    localStorage.setItem('recentlyViewed', JSON.stringify(updated));
  };

  const handleAddToCart = async (product) => {
    const qty = product.quantity || 1;
    
    try {
      // Try API first
      const result = await cartApi.addItem(product.id, qty, sessionId);
      
      // Update local state
      setCartItems((prev) => {
        const existing = prev.find((item) => item.id === product.id);
        if (existing) {
          return prev.map((item) =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + qty }
              : item
          );
        }
        return [...prev, { ...product, quantity: qty }];
      });
      
      toast.success(`${product.name} added to cart!`, {
        description: `Quantity: ${qty}`,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Fallback to local cart
      setCartItems((prev) => {
        const existing = prev.find((item) => item.id === product.id);
        if (existing) {
          return prev.map((item) =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + qty }
              : item
          );
        }
        return [...prev, { ...product, quantity: qty }];
      });
      toast.success(`${product.name} added to cart!`, {
        description: `Quantity: ${qty}`,
      });
    }
  };

  const handleUpdateQuantity = async (productId, quantity) => {
    try {
      await cartApi.updateItem(sessionId, productId, quantity, user?.id);
      setCartItems((prev) =>
        prev.map((item) =>
          item.id === productId ? { ...item, quantity } : item
        )
      );
    } catch (error) {
      console.error('Error updating cart:', error);
      // Still update local state
      setCartItems((prev) =>
        prev.map((item) =>
          item.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const handleRemoveFromCart = async (productId) => {
    try {
      await cartApi.removeItem(sessionId, productId, user?.id);
      setCartItems((prev) => prev.filter((item) => item.id !== productId));
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Error removing from cart:', error);
      // Still update local state
      setCartItems((prev) => prev.filter((item) => item.id !== productId));
      toast.success('Item removed from cart');
    }
  };

  const handleWishlist = async (productId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to add items to your wishlist');
      setShowAuthModal(true);
      return;
    }

    try {
      const isInWishlist = wishlistItems.includes(productId);
      
      if (isInWishlist) {
        await wishlistApi.remove(user.id, productId);
        setWishlistItems(prev => prev.filter(id => id !== productId));
        toast.success('Removed from wishlist');
      } else {
        await wishlistApi.add(user.id, productId);
        setWishlistItems(prev => [...prev, productId]);
        toast.success('Added to wishlist!');
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      toast.error('Failed to update wishlist');
    }
  };

  const handleQuickView = (product) => {
    setQuickViewProduct(product);
    trackRecentlyViewed(product.id);
    // Track view for recommendations engine
    try {
      recommendationsApi.trackView(product.id, user?.id, sessionId);
    } catch {}
  };

  // Handle content page click (for header/footer links)
  const handleContentPageClick = async (slug) => {
    try {
      const page = await contentApi.getPageBySlug(slug);
      setShowContentPage(page);
    } catch (error) {
      console.error('Error loading page:', error);
      toast.error('Page not found');
    }
  };

  // Handle checkout
  const handleCheckout = (data) => {
    setCheckoutData(data);
    setShowCheckout(true);
  };

  // Handle order complete
  const handleOrderComplete = (orderResult) => {
    // Clear cart
    setCartItems([]);
    localStorage.removeItem('cart_session_id');
    toast.success(`Order ${orderResult.order_number} placed successfully!`);
  };

  const handleFilterChange = ({ category, priceRange: pr, brands, minRating }) => {
    let filtered = [...products];
    if (category && category !== 'all') {
      const matchCat = categories.find(c => c.id === category || c.slug === category);
      filtered = filtered.filter((p) =>
        p.category === category || p.categorySlug === category ||
        (matchCat && (p.category === matchCat.id || p.categorySlug === matchCat.slug))
      );
    }
    if (pr) {
      filtered = filtered.filter((p) => p.price >= pr[0] && p.price <= pr[1]);
    }
    if (brands && brands.length > 0) {
      filtered = filtered.filter((p) => brands.includes(p.brand));
    }
    if (minRating && minRating > 0) {
      filtered = filtered.filter((p) => (p.rating || 0) >= minRating);
    }
    setFilteredProducts(filtered);
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
    let sorted = [...filteredProducts];
    switch (sort) {
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'best-seller':
        sorted.sort((a, b) => (b.is_best_seller ? 1 : 0) - (a.is_best_seller ? 1 : 0));
        break;
      default:
        break;
    }
    setFilteredProducts(sorted);
  };

  const handleAuthClick = () => {
    if (isAuthenticated) {
      if (isAdmin) {
        setShowAdminDashboard(true);
      } else if (isAffiliate) {
        setShowAffiliatePanel(true);
      } else {
        setShowCustomerDashboard(true);
      }
    } else {
      setShowAuthModal(true);
    }
  };

  const handleCategorySelect = (categoryIdOrSlug, subcategory) => {
    // Navigate to category page
    setActiveCategoryId(categoryIdOrSlug);
    setActiveCategorySlug(subcategory || categoryIdOrSlug);
    setActivePage('category');
    setActiveSearchQuery('');
    window.scrollTo(0, 0);
  };

  const navigateHome = () => {
    setActivePage(null);
    setActiveCategoryId(null);
    setActiveCategorySlug(null);
    setActiveSearchQuery('');
    refreshData();
    window.scrollTo(0, 0);
  };

  const handleFooterPageClick = (pageName) => {
    const pageSlugMap = {
      'About Us': 'about-us',
      'Contact Us': null,
      'Careers': null,
      'Press': null,
      'Help Center': 'faq',
      'Track Order': null,
      'Shipping Info': 'shipping-info',
      'Returns': 'returns-refunds',
      'Privacy Policy': 'privacy-policy',
      'Terms of Service': 'terms-conditions',
      'FAQ': 'faq',
    };

    const slug = pageSlugMap[pageName];
    
    if (pageName === 'Contact Us') {
      setShowContactModal(true);
    } else if (pageName === 'Track Order') {
      if (isAuthenticated) {
        if (isAdmin) setShowAdminDashboard(true);
        else setShowCustomerDashboard(true);
      } else {
        setShowAuthModal(true);
        toast.info('Please sign in to track your order');
      }
    } else if (pageName === 'Blog' || pageName === 'Health Blog') {
      setActivePage('blog'); window.scrollTo(0, 0);
    } else if (pageName === 'FAQ') {
      setActivePage('faq'); window.scrollTo(0, 0);
    } else if (pageName === 'All Products') {
      setActivePage('all-products'); window.scrollTo(0, 0);
    } else if (pageName === 'New Arrivals') {
      setActivePage('all-products'); window.scrollTo(0, 0);
    } else if (pageName === 'Careers') {
      handleContentPageClick('careers');
    } else if (pageName === 'Press') {
      handleContentPageClick('press');
    } else if (slug) {
      handleContentPageClick(slug);
    } else {
      toast.info(`${pageName} - Coming soon!`);
    }
  };

  // Show dashboards if user is logged in - wrapped in Suspense for lazy loading
  if (showAdminDashboard && isAdmin) {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <AdminDashboard onClose={() => { setShowAdminDashboard(false); refreshData(); }} />
      </Suspense>
    );
  }

  if (showCustomerDashboard && isCustomer) {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <CustomerDashboard onClose={() => setShowCustomerDashboard(false)} />
      </Suspense>
    );
  }

  if (showAffiliatePanel && isAffiliate) {
    return (
      <Suspense fallback={<DashboardLoader />}>
        <AffiliateDashboard onClose={() => setShowAffiliatePanel(false)} />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      <SkipToMain />
      <ScrollProgress />
      <TopBar onHeaderLinkClick={handleContentPageClick} />
      <Header
        cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        wishlistCount={wishlistItems.length}
        onCartClick={() => setShowCart(true)}
        onAuthClick={handleAuthClick}
        onQuickView={handleQuickView}
        onCategorySelect={handleCategorySelect}
        onViewAllProducts={() => { setActivePage('all-products'); window.scrollTo(0, 0); }}
        user={user}
        categories={categories}
        onSearch={(q) => setActiveSearchQuery(q)}
      />
      <FlashSaleBanner />

      {/* Search Results View */}
      {activeSearchQuery && (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" /></div>}>
          <SearchResults
            query={activeSearchQuery}
            onClose={() => setActiveSearchQuery('')}
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onWishlist={handleWishlist}
            wishlistItems={wishlistItems}
          />
        </Suspense>
      )}

      {/* Category Page */}
      {!activeSearchQuery && activePage === 'category' && (
        <Suspense fallback={<DashboardLoader />}>
          <CategoryPage
            categoryId={activeCategoryId}
            categorySlug={activeCategorySlug}
            onBack={navigateHome}
            onAddToCart={handleAddToCart}
            onWishlist={handleWishlist}
            onQuickView={handleQuickView}
            refreshTrigger={productsVersion}
          />
        </Suspense>
      )}

      {/* All Products / Featured / Best Sellers Page */}
      {!activeSearchQuery && (activePage === 'all-products' || activePage === 'featured' || activePage === 'best-sellers') && (
        <Suspense fallback={<DashboardLoader />}>
          <AllProductsPage
            title={activePage === 'featured' ? 'Featured Products' : activePage === 'best-sellers' ? 'Best Sellers' : 'All Products'}
            filter={activePage === 'featured' ? 'featured' : activePage === 'best-sellers' ? 'best_seller' : undefined}
            onBack={navigateHome}
            onAddToCart={handleAddToCart}
            onWishlist={handleWishlist}
            onQuickView={handleQuickView}
            refreshTrigger={productsVersion}
          />
        </Suspense>
      )}

      {/* Blog Page */}
      {!activeSearchQuery && activePage === 'blog' && (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-6">
          <div className="max-w-7xl mx-auto px-4">
            <button onClick={navigateHome} className="flex items-center gap-2 text-sm text-teal-600 mb-4 hover:text-teal-700">
              ← Back to Home
            </button>
          </div>
          <Suspense fallback={<DashboardLoader />}><HealthTipsBlog /></Suspense>
        </div>
      )}

      {/* FAQ Page */}
      {!activeSearchQuery && activePage === 'faq' && (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-6">
          <div className="max-w-7xl mx-auto px-4">
            <button onClick={navigateHome} className="flex items-center gap-2 text-sm text-teal-600 mb-4 hover:text-teal-700">
              ← Back to Home
            </button>
          </div>
          <Suspense fallback={<DashboardLoader />}><FAQSection /></Suspense>
        </div>
      )}
      
      {/* HOMEPAGE */}
      {!activeSearchQuery && !activePage && (
      <HomePageSections
        user={user}
        featuredProducts={featuredProducts}
        bestSellers={bestSellers}
        allProducts={products}
        handleAddToCart={handleAddToCart}
        handleWishlist={handleWishlist}
        handleQuickView={handleQuickView}
        handleCategorySelect={handleCategorySelect}
        setActivePage={setActivePage}
        wishlistItems={wishlistItems}
      />
      )}

      <Footer 
        onContactClick={() => setShowContactModal(true)}
        onPageClick={handleFooterPageClick}
      />

      {/* Modals & Drawers */}
      <WelcomeModal
        isOpen={showWelcomeModal && !showAuthModal}
        onClose={() => setShowWelcomeModal(false)}
      />
      <CartDrawer
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemove={handleRemoveFromCart}
        onCheckout={handleCheckout}
        sessionId={sessionId}
      />
      <QuickViewModal
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        product={quickViewProduct}
        onAddToCart={handleAddToCart}
        onWishlist={(product) => handleWishlist(product.id)}
        wishlistItems={wishlistItems.map(id => ({ product_id: id }))}
      />
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
      {showContentPage && (
        <ContentPageModal
          isOpen={!!showContentPage}
          onClose={() => setShowContentPage(null)}
          page={showContentPage}
        />
      )}
      {showCheckout && (
        <Suspense fallback={<DashboardLoader />}>
          <CheckoutPage
            cartData={checkoutData}
            user={user}
            onClose={() => setShowCheckout(false)}
            onOrderComplete={handleOrderComplete}
            sessionId={sessionId}
            onSignIn={() => setShowAuthModal(true)}
          />
        </Suspense>
      )}
      
      {/* Live Purchase Notification - Social Proof */}
      <LivePurchaseNotification />
      
      {/* AI Chatbot - Replace basic ChatWidget */}
      <Suspense fallback={null}>
        <AIChatbot user={user} />
      </Suspense>
      
      {/* Floating Actions */}
      <FloatingActions onContactClick={() => setShowContactModal(true)} />
      
      {/* Accessibility Button & Panel */}
      <AccessibilityButton onClick={() => setShowAccessibilityPanel(true)} />
      <AccessibilityPanel 
        isOpen={showAccessibilityPanel} 
        onClose={() => setShowAccessibilityPanel(false)} 
      />
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        wishlistCount={wishlistItems.length}
        onCartClick={() => setShowCart(true)}
        onAuthClick={handleAuthClick}
        onSearchClick={() => {
          document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });
        }}
        onHomeClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onWishlistClick={() => {
          if (isAuthenticated) {
            setShowCustomerDashboard(true);
          } else {
            toast.info('Please sign in to view your wishlist');
            setShowAuthModal(true);
          }
        }}
      />
      
      {/* PWA Install Prompt */}
      <Suspense fallback={null}>
        <PWAInstallPrompt />
      </Suspense>
      
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <AccessibilityProvider>
            <div className="App">
              <BrowserRouter>
                <GeoGate>
                  <Routes>
                    <Route path="/*" element={<HomePageContent />} />
                  </Routes>
                </GeoGate>
              </BrowserRouter>
            </div>
          </AccessibilityProvider>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
